import { mergeByProp } from '@shared/utils';
import findPendingUserReplacementIndex from '@/lib/activityEvents/findPendingUserReplacementIndex.js';
import getEventSeq from '@/lib/activityEvents/getPieveActivityEventSeq.js';
import mergeHydratedEvents from '@/lib/activityEvents/mergeHydratedActivityEvents.js';
import preserveExistingInteractionResponsePresentation from '@/lib/activityEvents/preserveExistingInteractionResponsePresentation.js';
import shouldPreserveExistingEventLines from '@/lib/activityEvents/shouldPreserveExistingEventLines.js';
import shouldPreserveExistingInteractionResponseSummary from '@/lib/activityEvents/shouldPreserveExistingInteractionResponseSummary.js';
import sortEvents from '@/lib/activityEvents/sortPieveActivityEvents.js';
import normalizeAppsMetadataItems from '@/lib/apps/normalizeAppsMetadataItems.js';
import { NON_RUNNING_RUN_STATES } from '@/lib/pieveRunState.js';
import { createEmptyPieveRuntimeSurface } from '@/lib/platform/pieveRuntimeCapabilities.js';
import getInitialRuntimeSinglePage from '@/redux/reducers/pieveReducer/getInitialRuntimeSinglePage.js';
import getInitialRuntimeSinglePageRoot from '@/redux/reducers/pieveReducer/getInitialRuntimeSinglePageRoot.js';
import isDeepEqual from '@/lib/isDeepEqual.js';
import isSkillKnowledgePath from '@/lib/isSkillKnowledgePath.js';
import normalizeProjectId from '@/lib/normalizeProjectId.js';
import isIncomingThreadMetadataStale from '@/lib/threads/isIncomingThreadMetadataStale.js';
import shouldPreserveExistingRunStateDuringHydration from '@/lib/threads/shouldPreserveExistingRunStateDuringHydration.js';
import buildThreadContentItems from '@/lib/threads/buildThreadContentItems.js';
import getThreadContentItemByThreadId from '@/lib/threads/getThreadContentItemByThreadId.js';
import getFirstValidThreadTimestamp from '@/lib/threads/getFirstValidThreadTimestamp.js';
import filterThreadsForMainScope from '@/lib/threads/filterThreadsForMainScope.js';
import getThreadId from '@/lib/threads/getThreadId.js';
import getThreadParentId from '@/lib/threads/getThreadParentId.js';
import getThreadProjectId from '@/lib/threads/getThreadProjectId.js';
import getThreadsById from '@/lib/threads/getThreadsById.js';
import normalizeThreadItems from '@/lib/threads/normalizeThreadItems.js';
import toThreadTimestampMs from '@/lib/threads/toThreadTimestampMs.js';
import { reconcileToolCallEventsWithToolResults } from '@/lib/threadEvents/toolCallActivityHelpers.js';
import reconcileHydratedThreads from '@/lib/threads/reconcileHydratedThreads.js';
import removeThread from '@/lib/threads/removeThread.js';
import replaceThreadContentItemsForScope from '@/lib/threads/replaceThreadContentItemsForScope.js';
import replaceThreadsForScope from '@/lib/threads/replaceThreadsForScope.js';
import getWorkspaceName from '@/lib/threads/getThreadWorkspaceName.js';
import normalizeThreadListScope from '@/lib/threads/normalizeThreadListScope.js';
import normalizeTriggerItemId from '@/lib/threads/normalizeTriggerItemId.js';
import removeThreadContentItem from '@/lib/threads/removeThreadContentItem.js';
import upsertSortedThread from '@/lib/threads/upsertSortedThread.js';
import upsertThreadContentItem from '@/lib/threads/upsertThreadContentItem.js';
import resolvePieveApiBaseUrl from '@/lib/resolvePieveApiBaseUrl.js';
import applyArchiveThreadFail from '@/redux/reducers/pieveReducer/applyArchiveThreadFail.js';
import mergeWorkflowSnapshotRows from '@/redux/reducers/pieveReducer/mergeWorkflowSnapshotRows.js';
import applyThreadActionsPayload from '@/redux/reducers/pieveReducer/applyThreadActionsPayload.js';
import applyThreadComposerMutation from '@/redux/reducers/pieveReducer/applyThreadComposerMutation.js';
import applyThreadDebugModeMutation from '@/redux/reducers/pieveReducer/applyThreadDebugModeMutation.js';
import applyThreadAppConnectionClearSuccess from '@/redux/reducers/pieveReducer/applyThreadAppConnectionClearSuccess.js';
import applyThreadAppConnectionSelectionSuccess from '@/redux/reducers/pieveReducer/applyThreadAppConnectionSelectionSuccess.js';
import areRuntimeMetadataValuesEqual from '@/redux/reducers/pieveReducer/areRuntimeMetadataValuesEqual.js';
import applyThreadPinMutation from '@/redux/reducers/pieveReducer/applyThreadPinMutation.js';
import applyThreadSandboxModeMutation from '@/redux/reducers/pieveReducer/applyThreadSandboxModeMutation.js';
import isSiblingArtifactPreview from '@/redux/reducers/pieveReducer/isSiblingArtifactPreview.js';
import applyThreadTitleMutation from '@/redux/reducers/pieveReducer/applyThreadTitleMutation.js';
import applySidebarOrganizationOperation from '@/redux/reducers/pieveReducer/applySidebarOrganizationOperation.js';
import applySidebarOrganizationOperations from '@/redux/reducers/pieveReducer/applySidebarOrganizationOperations.js';
import applyCommittedSidebarPlacement from '@/redux/reducers/pieveReducer/applyCommittedSidebarPlacement.js';
import normalizeSidebarOrganizationSnapshot from '@/redux/reducers/pieveReducer/normalizeSidebarOrganizationSnapshot.js';
import applyCredentialRowMutation from '@/redux/reducers/pieveReducer/applyCredentialRowMutation.js';
import mergeCredentialOverlay from '@/redux/reducers/pieveReducer/mergeCredentialOverlay.js';
import mergeCredentialUpsertSuccess from '@/redux/reducers/pieveReducer/mergeCredentialUpsertSuccess.js';
import buildVisibleProjectsFromPendingMutations from '@/redux/reducers/pieveReducer/buildVisibleProjectsFromPendingMutations.js';
import buildResetActiveCompanyScopeState from '@/redux/reducers/pieveReducer/buildResetActiveCompanyScopeState.js';
import buildThreadContentMap from '@/redux/reducers/pieveReducer/buildThreadContentMap.js';
import buildThreadContentShell from '@/redux/reducers/pieveReducer/buildThreadContentShell.js';
import ensureThreadContentItem from '@/redux/reducers/pieveReducer/ensureThreadContentItem.js';
import mapPublicThreadToRegularThread from '@/redux/reducers/pieveReducer/mapPublicThreadToRegularThread.js';
import getScopedExistingArray from '@/redux/reducers/pieveReducer/getScopedExistingArray.js';
import migratePersistedPieveStateV2 from '@/redux/reducers/pieveReducer/migratePersistedPieveStateV2.js';
import normalizeComposerDraftEntry from '@/redux/reducers/pieveReducer/normalizeComposerDraftEntry.js';
import normalizePieveProjectPendingMutation from '@/redux/reducers/pieveReducer/normalizePieveProjectPendingMutation.js';
import reconcilePersistedAutomationThreads from '@/redux/reducers/pieveReducer/reconcilePersistedAutomationThreads.js';
import replaceScheduleItemsFromRequestPayload from '@/redux/reducers/pieveReducer/replaceScheduleItemsFromRequestPayload.js';
import removeTriggerItemFromRequestPayload from '@/redux/reducers/pieveReducer/removeTriggerItemFromRequestPayload.js';
import replaceTriggerItemsFromRequestPayload from '@/redux/reducers/pieveReducer/replaceTriggerItemsFromRequestPayload.js';
import resolveCollectionScopeCompanyId from '@/redux/reducers/pieveReducer/resolveCollectionScopeCompanyId.js';
import upsertScheduleItemFromRequestPayload from '@/redux/reducers/pieveReducer/upsertScheduleItemFromRequestPayload.js';
import upsertTriggerItemFromRequestPayload from '@/redux/reducers/pieveReducer/upsertTriggerItemFromRequestPayload.js';
import rollbackConnectionOutcome from '@/redux/reducers/pieveReducer/rollbackConnectionOutcome.js';
import createPersistedReducer from '@/redux/lib/createPersistedReducer.js';

const THREAD_LIST_SCOPE_MAIN = 'main';

// Agents: Do not place helper functions in this file.

const initialState = {
  sidebar: {
    recentItems: [],
    workspaceLabel: 'Tasks',
    workspaceName: 'Workspace'
  },
  composerDraft: '',
  composerDraftsByContextKey: {},
  runtime: {
    kind: '',
    platform: '',
    surface: createEmptyPieveRuntimeSurface(),
    apiBaseUrl: resolvePieveApiBaseUrl(),
    /* Set by the address at boot and never by an action: the shape of the visit is decided by
       whoever opened it, and runtime metadata merges leave it alone. */
    singlePage: getInitialRuntimeSinglePage(),
    singlePageRoot: getInitialRuntimeSinglePageRoot(),
    branchName: '',
    localLabel: 'Local',
    accessLabel: 'Workspace-write'
  },
  activeCompanyScopeCompanyId: null,
  sidebarOrganizationByType: {
    home: {
      sidebarType: 'home',
      groups: [],
      folders: [],
      placements: [],
      scopeCompanyId: null,
      loaded: false
    },
    automation: {
      sidebarType: 'automation',
      groups: [],
      folders: [],
      placements: [],
      scopeCompanyId: null,
      loaded: false
    }
  },
  sidebarOrganizationServerPayloadByType: {
    home: null,
    automation: null
  },
  sidebarOrganizationLastSettledLocalChangeAtByType: {
    home: 0,
    automation: 0
  },
  sidebarOrganizationPendingMutations: [],
  projects: [],
  projectsServerPayload: [],
  projectPendingMutations: [],
  workflowStepPendingEdits: [],
  sidebarSearchThreads: [],
  scheduleItems: [],
  scheduleItemsScopeCompanyId: null,
  scheduleRunList: [],
  automationRunEvents: [],
  scheduleRunDailyCounts: [],
  scheduleOperationsList: [],
  scheduleRecentActivityChartDailyCounts: [],
  triggerItems: [],
  triggerItemsScopeCompanyId: null,
  triggerRunList: [],
  triggerRunDailyCounts: [],
  triggerOperationsList: [],
  triggerRecentActivityChartDailyCounts: [],
  triggerPollEvents: [],
  triggerAppActionsByAppRef: {},
  triggerAppActionErrorsByAppRef: {},
  triggerAppActionsLoadedByAppRef: {},
  threadToolCallEvents: [],
  workflows: [],
  workflowsScopeCompanyId: null,
  // main.company_environment rows, as returned. Company-scoped, and a company switch resets the whole
  // slice to initialState, so this needs no scope stamp of its own.
  companyEnvironments: [],
  workflowConnections: [],
  workflowConnectionsWorkflowId: null,
  workflowWebhookSamples: [],
  workflowWebhookSamplesWorkflowId: null,
  workflowStepOutputs: [],
  workflowStepTasks: [],
  // The Test window's chat thread per workflow ({ [workflowId]: threadId }), from POST .../test-thread.
  workflowTestThreadIdByWorkflowId: {},
  workflowRunList: [],
  // Run history paging per workflow ({ [workflowId]: { nextCursor, totalCount } }), from the first page's response meta.
  workflowRunListPaging: {},
  workflowRunSearch: null,
  workflowRunDailyCountsList: [],
  workflowRunTimeMachineList: [],
  // Governance ledger rows (changes) for the runs whose logs were opened, each carrying its `runId`.
  workflowRunOperationsList: [],
  // The workflow detail page's Recent activity table and chart, for the route-selected date window.
  workflowOperationsList: [],
  workflowRecentActivityChartDailyCounts: [],
  workflowVersionsList: [],
  publicThread: null,
  threads: [],
  threadsScopeCompanyId: null,
  mainThreadSectionCursors: {},
  companyThreads: [],
  companyThreadsScopeCompanyId: null,
  threadContent: [],
  appsMetadataItems: [],
  // `main.app_version` rows (version, isPublished, createdOn, appId) for the apps whose step version picker was opened.
  appVersions: [],
  appsMetadataEtag: null,
  /* Pieve caches the shown-on-Pieve metadata required by the shared visibility predicate. */
  appsMetadataIncludesHidden: false,
  appsMetadataLoaded: false,
  operationsDashboardResponse: null,
  operationsFeedResponse: null,
  operationsSearchResponse: null,
  /* Full Service (MindCloud admin only): the project list and the one project detail last opened.
     Cross-company by nature, so neither carries a company scope stamp; the detail is gated by the
     route's projectId in selectors instead. */
  knowledge: [],
  connections: [],
  connectionsServerPayload: [],
  connectionPendingRemovalsByCredentialId: {},
  companyConnections: [],
  currentCredential: null,
  /* Full app definitions (authentication, actions) opened on the connections screen, one per app. */
  appDefinitions: [],
  /* Files of the organization knowledge folder, as listed by the knowledge files route. */
  knowledgeFiles: [],
  currentCredentialLoadCredentialId: null,
  currentCredentialLoadRequestId: null,
  // The person's archived conversations (`data.threads` of GET /v1/pieve/archived-threads), as returned.
  archivedThreads: [],
  archivedThreadSnapshotByThreadId: {},
  archivedThreadPendingRequestIdByThreadId: {},
  approvalDecisionServerStateByKey: {},
  approvalDecisionPendingRequestIdByKey: {}
};

export default createPersistedReducer(
  initialState,
  {
    GET_PUBLIC_THREAD_SHARE_SUCCESS: (state, payload) => mapPublicThreadToRegularThread({ state, data: payload?.data }),

    GET_PUBLIC_THREAD_SHARE_FAIL: (state, payload) => ({ ...state, publicThread: payload?.data || null }),

    pieveClearPublicThread: (state) => {
      if (state?.publicThread === null) {
        return state;
      }

      return {
        ...state,
        publicThread: null
      };
    },

    RESET_PIEVE_ACTIVE_COMPANY_SCOPE: (state, payload) => {
      return buildResetActiveCompanyScopeState({
        state,
        initialState,
        payload
      });
    },

    // Sign-out and user replacement must not retain another person's switches or request identities.

    GET_SIDEBAR_ORGANIZATION_SUCCESS: (state, payload) => {
      const snapshot = payload?.data || null;

      if (!snapshot) {
        return state;
      }

      const sidebarType = payload?.meta?.sidebarType || snapshot.sidebarType;
      const requestedAt = Number(payload?.meta?.requestedAt || 0);
      const lastSettledAt = Number(state.sidebarOrganizationLastSettledLocalChangeAtByType?.[sidebarType] || 0);

      if (requestedAt > 0 && requestedAt < lastSettledAt) {
        return state;
      }

      const scopedSnapshot = normalizeSidebarOrganizationSnapshot({ snapshot, companyId: payload?.meta?.companyId });
      const pendingMutations = state.sidebarOrganizationPendingMutations.filter((pendingMutation) => pendingMutation.sidebarType === sidebarType);

      return {
        ...state,
        sidebarOrganizationServerPayloadByType: {
          ...state.sidebarOrganizationServerPayloadByType,
          [sidebarType]: scopedSnapshot
        },
        sidebarOrganizationByType: {
          ...state.sidebarOrganizationByType,
          [sidebarType]: applySidebarOrganizationOperations({ snapshot: scopedSnapshot, pendingMutations })
        }
      };
    },

    PUT_SIDEBAR_ORGANIZATION_OPTIMISTIC: (state, payload) => {
      const requestId = payload?.meta?.requestId;
      const sidebarType = payload?.meta?.sidebarType;

      if (!requestId || !sidebarType) {
        return state;
      }

      const operation = Object.fromEntries(Object.entries(payload || {}).filter(([key]) => key !== 'meta'));
      const pendingMutation = { requestId, sidebarType, localChangeAt: Number(payload?.meta?.localChangeAt || Date.now()), operation };

      return {
        ...state,
        sidebarOrganizationPendingMutations: [...state.sidebarOrganizationPendingMutations, pendingMutation],
        sidebarOrganizationByType: {
          ...state.sidebarOrganizationByType,
          [sidebarType]: applySidebarOrganizationOperation({ snapshot: state.sidebarOrganizationByType?.[sidebarType], operation })
        }
      };
    },

    PUT_SIDEBAR_ORGANIZATION_SUCCESS: (state, payload) => {
      const requestId = payload?.meta?.requestId;
      const sidebarType = payload?.meta?.sidebarType || payload?.data?.sidebarType;
      const snapshot = payload?.data || null;
      const settledMutation = state.sidebarOrganizationPendingMutations.find((pendingMutation) => pendingMutation.requestId === requestId);
      const settledAt = Number(payload?.meta?.localChangeAt || settledMutation?.localChangeAt || 0);
      const lastSettledAt = Number(state.sidebarOrganizationLastSettledLocalChangeAtByType?.[sidebarType] || 0);
      const isStale = settledAt < lastSettledAt;
      const pendingMutations = state.sidebarOrganizationPendingMutations.filter((pendingMutation) => pendingMutation.requestId !== requestId);

      if (!snapshot || !sidebarType) {
        return { ...state, sidebarOrganizationPendingMutations: pendingMutations };
      }

      const currentOrganization = state.sidebarOrganizationByType?.[sidebarType];
      const currentServerSnapshot = state.sidebarOrganizationServerPayloadByType?.[sidebarType] || currentOrganization;
      const scopedSnapshot = isStale ? currentServerSnapshot : normalizeSidebarOrganizationSnapshot({ snapshot, companyId: payload?.meta?.companyId || currentOrganization?.scopeCompanyId });
      const sidebarPendingMutations = pendingMutations.filter((pendingMutation) => pendingMutation.sidebarType === sidebarType);

      return {
        ...state,
        sidebarOrganizationServerPayloadByType: {
          ...state.sidebarOrganizationServerPayloadByType,
          [sidebarType]: scopedSnapshot
        },
        sidebarOrganizationLastSettledLocalChangeAtByType: isStale
          ? state.sidebarOrganizationLastSettledLocalChangeAtByType
          : {
              ...state.sidebarOrganizationLastSettledLocalChangeAtByType,
              [sidebarType]: settledAt
            },
        sidebarOrganizationPendingMutations: pendingMutations,
        sidebarOrganizationByType: {
          ...state.sidebarOrganizationByType,
          [sidebarType]: applySidebarOrganizationOperations({ snapshot: scopedSnapshot, pendingMutations: sidebarPendingMutations })
        }
      };
    },

    PUT_SIDEBAR_ORGANIZATION_FAIL: (state, payload) => {
      const requestId = payload?.meta?.requestId;
      const sidebarType = payload?.meta?.sidebarType;
      const pendingMutations = state.sidebarOrganizationPendingMutations.filter((pendingMutation) => pendingMutation.requestId !== requestId);

      if (!sidebarType) {
        return { ...state, sidebarOrganizationPendingMutations: pendingMutations };
      }

      const serverSnapshot = state.sidebarOrganizationServerPayloadByType?.[sidebarType] || state.sidebarOrganizationByType?.[sidebarType];
      const sidebarPendingMutations = pendingMutations.filter((pendingMutation) => pendingMutation.sidebarType === sidebarType);

      return {
        ...state,
        sidebarOrganizationPendingMutations: pendingMutations,
        sidebarOrganizationByType: {
          ...state.sidebarOrganizationByType,
          [sidebarType]: applySidebarOrganizationOperations({ snapshot: serverSnapshot, pendingMutations: sidebarPendingMutations })
        }
      };
    },

    pieveSetRuntimeMetadata: (state, payload) => {
      const nextRuntimeMetadata = {
        kind: String(payload?.kind || '')
          .trim()
          .toLowerCase(),
        platform: String(payload?.platform || '')
          .trim()
          .toLowerCase(),
        surface: payload?.surface || createEmptyPieveRuntimeSurface(),
        apiBaseUrl: String(payload?.apiBaseUrl || state?.runtime?.apiBaseUrl || '')
          .trim()
          .replace(/\/$/, '')
      };

      if (areRuntimeMetadataValuesEqual(state.runtime, nextRuntimeMetadata)) {
        return state;
      }

      return {
        ...state,
        runtime: {
          ...state.runtime,
          ...nextRuntimeMetadata
        }
      };
    },

    pieveSetActiveProjectId: (state, payload) => {
      const activeId = normalizeProjectId(payload);

      if (!activeId || !state.projects?.length) {
        return state;
      }

      return {
        ...state,
        projects: state.projects.map((p) => ({
          ...p,
          isDefault: normalizeProjectId(p.id) === activeId
        }))
      };
    },

    pieveSetComposerDraftForContext: (state, payload) => {
      const normalizedEntry = normalizeComposerDraftEntry(payload);

      if (!normalizedEntry?.contextKey) {
        return state;
      }

      if (!normalizedEntry.value) {
        if (!state?.composerDraftsByContextKey?.[normalizedEntry.contextKey]) {
          return state;
        }

        const nextComposerDraftsByContextKey = {
          ...(state.composerDraftsByContextKey || {})
        };

        delete nextComposerDraftsByContextKey[normalizedEntry.contextKey];

        return {
          ...state,
          composerDraftsByContextKey: nextComposerDraftsByContextKey
        };
      }

      const previousEntry = state?.composerDraftsByContextKey?.[normalizedEntry.contextKey];

      if (previousEntry?.value === normalizedEntry.value && previousEntry?.kind === normalizedEntry.kind && previousEntry?.threadId === normalizedEntry.threadId && previousEntry?.projectId === normalizedEntry.projectId) {
        return state;
      }

      return {
        ...state,
        composerDraftsByContextKey: {
          ...(state.composerDraftsByContextKey || {}),
          [normalizedEntry.contextKey]: normalizedEntry
        }
      };
    },

    pieveSetAppsMetadataItems: (state, payload) => {
      const incoming = normalizeAppsMetadataItems(payload?.items);

      return {
        ...state,
        appsMetadataItems: incoming,
        appsMetadataEtag: String(payload?.etag || '').trim() || null,
        appsMetadataIncludesHidden: payload?.includesHidden === true,
        appsMetadataLoaded: true
      };
    },

    LIST_APP_VERSIONS_SUCCESS: (state, payload) => {
      const appId = payload.meta.appId;

      return { ...state, appVersions: [...state.appVersions.filter((row) => row.appId !== appId), ...payload.data] };
    },

    pieveConfirmAppsMetadataItems: (state) => {
      if (!Array.isArray(state?.appsMetadataItems) || state.appsMetadataItems.length === 0) {
        return state;
      }

      return {
        ...state,
        appsMetadataLoaded: true
      };
    },

    GET_PIEVE_OPERATIONS_DASHBOARD_SUCCESS: (state, payload) => {
      return {
        ...state,
        operationsDashboardResponse: payload || null
      };
    },

    GET_PIEVE_OPERATIONS_DASHBOARD_FAIL: (state, payload) => {
      return {
        ...state,
        operationsDashboardResponse: payload || null
      };
    },

    GET_PIEVE_OPERATIONS_FEED_SUCCESS: (state, payload) => {
      return {
        ...state,
        operationsFeedResponse: payload || null
      };
    },

    GET_PIEVE_OPERATIONS_FEED_FAIL: (state, payload) => {
      return {
        ...state,
        operationsFeedResponse: payload || null
      };
    },

    SEARCH_PIEVE_OPERATIONS_SUCCESS: (state, payload) => {
      return {
        ...state,
        operationsSearchResponse: payload || null
      };
    },

    SEARCH_PIEVE_OPERATIONS_FAIL: (state, payload) => {
      return {
        ...state,
        operationsSearchResponse: payload || null
      };
    },

    GET_PIEVE_TRIGGER_APP_ACTIONS_SUCCESS: (state, payload) => {
      const appId = String(payload?.meta?.appId || '').trim();

      if (!appId) {
        return state;
      }

      return {
        ...state,
        triggerAppActionsByAppRef: {
          ...(state?.triggerAppActionsByAppRef || {}),
          [appId]: Array.isArray(payload?.data) ? payload.data : []
        },
        triggerAppActionsLoadedByAppRef: {
          ...(state?.triggerAppActionsLoadedByAppRef || {}),
          [appId]: true
        },
        triggerAppActionErrorsByAppRef: {
          ...(state?.triggerAppActionErrorsByAppRef || {}),
          [appId]: ''
        }
      };
    },

    GET_PIEVE_TRIGGER_APP_ACTIONS_FAIL: (state, payload) => {
      const appId = String(payload?.meta?.appId || '').trim();

      if (!appId) {
        return state;
      }

      return {
        ...state,
        triggerAppActionsLoadedByAppRef: {
          ...(state?.triggerAppActionsLoadedByAppRef || {}),
          [appId]: false
        },
        triggerAppActionErrorsByAppRef: {
          ...(state?.triggerAppActionErrorsByAppRef || {}),
          [appId]: String(payload?.message || 'Failed to load trigger actions')
        }
      };
    },
    GET_SKILLS_SUCCESS: (state, payload) => {
      return {
        ...state,
        knowledge: mergeByProp(
          'id',
          state.knowledge.filter((row) => !isSkillKnowledgePath(row?.path)),
          Array.isArray(payload?.data) ? payload.data : []
        )
      };
    },

    GET_CONNECTIONS_SUCCESS: (state, payload) => {
      const serverConnections = Array.isArray(payload?.data) ? payload.data : [];
      const pendingRemovals = state?.connectionPendingRemovalsByCredentialId || {};
      const hiddenCredentialIds = new Set(Object.keys(pendingRemovals).filter(Boolean));

      return {
        ...state,
        connections: serverConnections.filter((item) => !hiddenCredentialIds.has(String(item?.credentialId || '').trim())),
        connectionsServerPayload: serverConnections
      };
    },

    GET_COMPANY_CONNECTIONS_SUCCESS: (state, payload) => {
      return {
        ...state,
        companyConnections: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    PUT_CREDENTIAL_ROLE_OPTIMISTIC: (state, payload) => {
      const credentialId = String(payload?.meta?.credentialId || '').trim();
      const roleId = String(payload?.meta?.roleId || '').trim();

      if (!credentialId || !roleId) {
        return state;
      }

      const isActive = Boolean(payload?.meta?.isActive);
      const currentCompanyConnections = Array.isArray(state?.companyConnections) ? state.companyConnections : [];
      const nextCompanyConnections = currentCompanyConnections.map((connection) => {
        if (String(connection?.credentialId || '').trim() !== credentialId) {
          return connection;
        }

        const currentAssignments = Array.isArray(connection?.roleAssignments) ? connection.roleAssignments : [];
        const existingAssignment = currentAssignments.find((assignment) => String(assignment?.roleId || '').trim() === roleId);

        if (existingAssignment) {
          return {
            ...connection,
            roleAssignments: currentAssignments.map((assignment) => {
              return String(assignment?.roleId || '').trim() === roleId ? { ...assignment, isActive } : assignment;
            })
          };
        }

        return {
          ...connection,
          roleAssignments: [...currentAssignments, { roleId, roleName: null, isActive }]
        };
      });

      return {
        ...state,
        companyConnections: nextCompanyConnections
      };
    },

    REMOVE_CONNECTION_OPTIMISTIC: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const credentialId = String(payload?.meta?.credentialId || '').trim();

      if (!requestId || !credentialId) {
        return state;
      }

      const serverConnections = Array.isArray(state?.connectionsServerPayload) ? state.connectionsServerPayload : state.connections;
      const previousConnections = Array.isArray(serverConnections) ? serverConnections : [];
      const previousIndex = previousConnections.findIndex((connection) => String(connection?.credentialId || '').trim() === credentialId);

      if (previousIndex < 0) {
        return state;
      }

      const previousConnection = previousConnections[previousIndex];
      const nextPendingRemovalsByCredentialId = {
        ...(state?.connectionPendingRemovalsByCredentialId || {}),
        [credentialId]: {
          requestId,
          index: previousIndex,
          connection: previousConnection
        }
      };

      return {
        ...state,
        connections: previousConnections.filter((connection) => String(connection?.credentialId || '').trim() !== credentialId),
        connectionsServerPayload: previousConnections,
        connectionPendingRemovalsByCredentialId: nextPendingRemovalsByCredentialId
      };
    },

    REMOVE_CONNECTION_SUCCESS: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const credentialId = String(payload?.meta?.credentialId || '').trim();
      const pendingRemoval = state?.connectionPendingRemovalsByCredentialId?.[credentialId] || null;

      if (!requestId || !credentialId || !pendingRemoval || pendingRemoval.requestId !== requestId) {
        return state;
      }

      const nextPendingRemovalsByCredentialId = { ...(state?.connectionPendingRemovalsByCredentialId || {}) };
      delete nextPendingRemovalsByCredentialId[credentialId];

      const nextServerPayload = (Array.isArray(state?.connectionsServerPayload) ? state.connectionsServerPayload : state.connections).filter((connection) => String(connection?.credentialId || '').trim() !== credentialId);
      const hiddenCredentialIds = new Set(Object.keys(nextPendingRemovalsByCredentialId).filter(Boolean));

      return {
        ...state,
        connections: nextServerPayload.filter((connection) => !hiddenCredentialIds.has(String(connection?.credentialId || '').trim())),
        connectionsServerPayload: nextServerPayload,
        connectionPendingRemovalsByCredentialId: nextPendingRemovalsByCredentialId
      };
    },

    SET_CURRENT_CREDENTIAL_LOAD_REQUEST: (state, payload) => {
      const credentialId = String(payload?.credentialId || '').trim();
      const loadRequestId = String(payload?.loadRequestId || '').trim();

      if (!credentialId || !loadRequestId) {
        return state;
      }

      return {
        ...state,
        currentCredential: null,
        currentCredentialLoadCredentialId: credentialId,
        currentCredentialLoadRequestId: loadRequestId
      };
    },

    GET_CREDENTIAL_SUCCESS: (state, payload) => {
      const credential = payload?.data?.item || null;
      const credentialId = String(credential?.credentialId || '').trim();
      const loadCredentialId = String(state?.currentCredentialLoadCredentialId || '').trim();
      const loadRequestId = String(payload?.meta?.loadRequestId || '').trim();

      if (!credential || !credentialId || !loadCredentialId || credentialId !== loadCredentialId || !loadRequestId || loadRequestId !== state?.currentCredentialLoadRequestId) {
        return state;
      }

      return {
        ...state,
        currentCredential: credential
      };
    },

    GET_CREDENTIAL_FAIL: (state) => {
      return state;
    },

    GET_APP_DEFINITION_SUCCESS: (state, payload) => {
      const app = payload?.data?.item || null;

      if (!app?.id) {
        return state;
      }

      return {
        ...state,
        appDefinitions: [...state.appDefinitions.filter((row) => row.id !== app.id), app]
      };
    },

    GET_KNOWLEDGE_FILES_SUCCESS: (state, payload) => {
      return {
        ...state,
        knowledgeFiles: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    REMOVE_KNOWLEDGE_FILE_SUCCESS: (state, payload) => {
      return {
        ...state,
        knowledgeFiles: state.knowledgeFiles.filter((file) => file.path !== payload?.meta?.path)
      };
    },

    UPSERT_CREDENTIAL_OPTIMISTIC: (state, payload) => {
      const credentialId = String(payload?.meta?.credentialId || payload?.credentialId || '').trim();

      if (!credentialId) {
        return state;
      }

      const current = state?.currentCredential;

      if (!current || String(current?.credentialId || '').trim() !== credentialId) {
        return state;
      }

      const overlayContext = {
        secretsOverlay: payload?.meta?.secretsOverlay || null,
        metaOverlay: payload?.meta?.metaOverlay || null
      };
      const nextCurrentCredential = mergeCredentialOverlay(current, overlayContext);

      if (nextCurrentCredential === current) {
        return state;
      }

      return {
        ...state,
        currentCredential: nextCurrentCredential,
        connectionsServerPayload: applyCredentialRowMutation(state?.connectionsServerPayload, credentialId, (row) => mergeCredentialOverlay(row, overlayContext)),
        connections: applyCredentialRowMutation(state?.connections, credentialId, (row) => mergeCredentialOverlay(row, overlayContext)),
        companyConnections: applyCredentialRowMutation(state?.companyConnections, credentialId, (row) => mergeCredentialOverlay(row, overlayContext))
      };
    },

    UPSERT_CREDENTIAL_SUCCESS: (state, payload) => {
      const serverCredentialId = String(payload?.data?.credentialId || payload?.meta?.credentialId || '').trim();
      const previousCredentialId = String(payload?.meta?.credentialId || '').trim();
      const current = state?.currentCredential;
      const currentId = String(current?.credentialId || '').trim();

      if (!current || !serverCredentialId) {
        return state;
      }

      if (currentId !== previousCredentialId && currentId !== serverCredentialId) {
        return state;
      }

      const overlayContext = {
        secretsOverlay: payload?.meta?.secretsOverlay || null,
        metaOverlay: payload?.meta?.metaOverlay || null
      };
      const serverCredential = payload?.data && typeof payload.data === 'object' ? payload.data : null;
      const nextCurrentCredential = mergeCredentialUpsertSuccess(current, {
        serverCredential,
        serverCredentialId,
        overlayContext
      });

      return {
        ...state,
        currentCredential: nextCurrentCredential,
        connectionsServerPayload: applyCredentialRowMutation(state?.connectionsServerPayload, previousCredentialId || serverCredentialId, (row) => mergeCredentialUpsertSuccess(row, { serverCredential, serverCredentialId, overlayContext })),
        connections: applyCredentialRowMutation(state?.connections, previousCredentialId || serverCredentialId, (row) => mergeCredentialUpsertSuccess(row, { serverCredential, serverCredentialId, overlayContext })),
        companyConnections: applyCredentialRowMutation(state?.companyConnections, previousCredentialId || serverCredentialId, (row) => mergeCredentialUpsertSuccess(row, { serverCredential, serverCredentialId, overlayContext }))
      };
    },

    UPSERT_CREDENTIAL_FAIL: (state) => {
      return state;
    },

    TEST_CREDENTIAL_SUCCESS: (state, payload) => {
      const credentialId = String(payload?.meta?.credentialId || '').trim();

      if (!credentialId) {
        return state;
      }

      const status = payload?.success === true ? 'valid' : 'invalid';
      const attemptedOn = new Date().toISOString();
      const statusPatch = { status, statusAsOf: attemptedOn, attemptedOn };
      const current = state?.currentCredential;
      const nextCurrentCredential = current && String(current?.credentialId || '').trim() === credentialId ? { ...current, ...statusPatch } : current;

      return {
        ...state,
        currentCredential: nextCurrentCredential,
        connectionsServerPayload: applyCredentialRowMutation(state?.connectionsServerPayload, credentialId, (row) => ({ ...row, ...statusPatch })),
        connections: applyCredentialRowMutation(state?.connections, credentialId, (row) => ({ ...row, ...statusPatch })),
        companyConnections: applyCredentialRowMutation(state?.companyConnections, credentialId, (row) => ({ ...row, ...statusPatch }))
      };
    },

    CLEAR_CURRENT_CREDENTIAL: (state) => {
      if (!state?.currentCredential) {
        return state;
      }

      return {
        ...state,
        currentCredential: null,
        currentCredentialLoadCredentialId: null,
        currentCredentialLoadRequestId: null
      };
    },

    REMOVE_CONNECTION_FAIL: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const credentialId = String(payload?.meta?.credentialId || '').trim();
      const pendingRemoval = state?.connectionPendingRemovalsByCredentialId?.[credentialId] || null;

      if (!requestId || !credentialId || !pendingRemoval || pendingRemoval.requestId !== requestId) {
        return state;
      }

      const nextPendingRemovalsByCredentialId = { ...(state?.connectionPendingRemovalsByCredentialId || {}) };
      delete nextPendingRemovalsByCredentialId[credentialId];

      const serverConnections = Array.isArray(state?.connectionsServerPayload) ? state.connectionsServerPayload : [];
      const hiddenCredentialIds = new Set(Object.keys(nextPendingRemovalsByCredentialId).filter(Boolean));

      return {
        ...state,
        connections: serverConnections.filter((connection) => !hiddenCredentialIds.has(String(connection?.credentialId || '').trim())),
        connectionPendingRemovalsByCredentialId: nextPendingRemovalsByCredentialId
      };
    },

    GET_SKILL_SUCCESS: (state, payload) => {
      return {
        ...state,
        knowledge: mergeByProp('id', state.knowledge, Array.isArray(payload?.data) ? payload.data : [])
      };
    },

    GET_ARCHIVED_THREADS_SUCCESS: (state, payload) => ({
      ...state,
      archivedThreads: Array.isArray(payload?.data?.threads) ? payload.data.threads : []
    }),

    RESTORE_THREAD_SUCCESS: (state, payload) => {
      const threadId = String(payload?.resourceId || payload?.meta?.resourceId || '').trim();
      return threadId ? { ...state, archivedThreads: state.archivedThreads.filter((row) => row.id !== threadId) } : state;
    },

    SEARCH_SIDEBAR_THREADS_SUCCESS: (state, payload) => ({
      ...state,
      sidebarSearchThreads: Array.isArray(payload?.data?.items) ? payload.data.items : []
    }),

    GET_SCHEDULES_SUCCESS: (state, payload) => {
      const nextState = replaceScheduleItemsFromRequestPayload({
        state,
        payload
      });

      return reconcilePersistedAutomationThreads({
        state: nextState,
        threadType: 'schedule',
        automationItems: nextState.scheduleItems,
        itemsScopeCompanyId: nextState.scheduleItemsScopeCompanyId
      });
    },

    GET_SCHEDULES_FAIL: (state) => {
      return state;
    },

    GET_SCHEDULE_RUN_LIST_SUCCESS: (state, payload) => {
      return {
        ...state,
        scheduleRunList: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    // The events of automation run conversations, as the API returns them, for the run history's steps and results.
    GET_AUTOMATION_RUN_EVENTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        automationRunEvents: mergeByProp('eventId', state.automationRunEvents, Array.isArray(payload?.data?.items) ? payload.data.items : [])
      };
    },

    GET_SCHEDULE_RUN_DAILY_COUNTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        scheduleRunDailyCounts: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_SCHEDULE_OPERATIONS_SUCCESS: (state, payload) => {
      return {
        ...state,
        scheduleOperationsList: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_SCHEDULE_RECENT_ACTIVITY_CHART_DAILY_COUNTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        scheduleRecentActivityChartDailyCounts: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_TRIGGER_RUN_LIST_SUCCESS: (state, payload) => {
      return {
        ...state,
        triggerRunList: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_TRIGGER_RUN_DAILY_COUNTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        triggerRunDailyCounts: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_TRIGGER_OPERATIONS_SUCCESS: (state, payload) => {
      return {
        ...state,
        triggerOperationsList: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_TRIGGER_RECENT_ACTIVITY_CHART_DAILY_COUNTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        triggerRecentActivityChartDailyCounts: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_TRIGGER_POLLS_SUCCESS: (state, payload) => {
      const incoming = Array.isArray(payload?.data?.polls) ? payload.data.polls : [];
      return {
        ...state,
        triggerPollEvents: mergeByProp('polled_at', state.triggerPollEvents, incoming)
      };
    },

    GET_THREAD_TOOL_CALL_SUCCESS: (state, payload) => {
      const incoming = Array.isArray(payload?.data) ? payload.data : [];
      return {
        ...state,
        threadToolCallEvents: mergeByProp('id', state.threadToolCallEvents, incoming)
      };
    },

    UPDATE_SCHEDULE_SUCCESS: (state, payload) => {
      return upsertScheduleItemFromRequestPayload({
        state,
        payload
      });
    },

    GET_TRIGGERS_SUCCESS: (state, payload) => {
      const nextState = replaceTriggerItemsFromRequestPayload({
        state,
        payload
      });

      return reconcilePersistedAutomationThreads({
        state: nextState,
        threadType: 'trigger',
        automationItems: nextState.triggerItems,
        itemsScopeCompanyId: nextState.triggerItemsScopeCompanyId
      });
    },

    GET_TRIGGERS_FAIL: (state) => {
      return state;
    },

    GET_TRIGGER_SUCCESS: (state, payload) => {
      return upsertTriggerItemFromRequestPayload({
        state,
        payload
      });
    },

    UPDATE_TRIGGER_SUCCESS: (state, payload) => {
      const row = Array.isArray(payload?.data) ? payload.data[0] : null;

      if (!row?.id) {
        return state;
      }

      const details = row.details && typeof row.details === 'object' ? row.details : {};
      const previous = Array.isArray(state?.triggerItems) ? state.triggerItems : [];
      const match = (item) => item?.id === row.id;

      return {
        ...state,
        triggerItems: previous.some(match)
          ? previous.map((item) => {
              if (!match(item)) {
                return item;
              }

              return {
                ...item,
                title: row.title,
                prompt: row.prompt,
                active: row.active,
                triggerType: row.triggerType,
                appId: row.appId,
                actionId: row.actionId,
                externalCompanyId: row.externalCompanyId,
                credentialId: row.credentialId,
                pollIntervalMinutes: row.pollIntervalMinutes,
                triggerSummary: row.triggerSummary,
                details,
                composerModel: details.composerModel || null,
                composerReasoningEffort: details.composerReasoningEffort || null,
                promptSummary: details.promptSummary || null,
                updatedAt: row.updatedOn || null
              };
            })
          : previous
      };
    },

    ARCHIVE_TRIGGER_SUCCESS: (state, payload) => {
      return removeTriggerItemFromRequestPayload({
        state,
        payload
      });
    },

    GET_KNOWLEDGE_BY_PATH_SUCCESS: (state, payload) => {
      return {
        ...state,
        knowledge: mergeByProp('path', state.knowledge, Array.isArray(payload?.data) ? payload.data : [])
      };
    },

    UPDATE_SKILL_SUCCESS: (state, payload) => {
      // Skill metadata PUT responses do not include markdown content. Preserve a
      // loaded detail record while applying the returned metadata snapshot.
      return {
        ...state,
        knowledge: mergeByProp('id', state.knowledge, Array.isArray(payload?.data) ? payload.data : [], (existing, incoming) => ({ ...existing, ...incoming }), { keepPosition: true })
      };
    },

    SET_SKILL_PINNED_SUCCESS: (state, payload) => {
      return {
        ...state,
        knowledge: mergeByProp('id', state.knowledge, Array.isArray(payload?.data) ? payload.data : [])
      };
    },

    ACCEPT_SHARED_SKILL_SUCCESS: (state, payload) => {
      return {
        ...state,
        knowledge: mergeByProp('id', state.knowledge, Array.isArray(payload?.data) ? payload.data : [])
      };
    },

    REMOVE_SKILL_SUCCESS: (state, payload) => {
      const skillId = String(payload?.meta?.skillId || '').trim();

      if (!skillId) {
        return state;
      }

      return {
        ...state,
        knowledge: state.knowledge.filter((item) => String(item?.id || '') !== skillId)
      };
    },

    GET_PROJECTS_SUCCESS: (state, payload) => {
      const projects = Array.isArray(payload?.data) ? payload.data : [];
      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects: projects,
          pendingMutations: state?.projectPendingMutations
        }),
        projectsServerPayload: projects
      };
    },

    CREATE_PROJECT_SUCCESS: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const nextPendingMutations = requestId ? (Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : []).filter((item) => item?.requestId !== requestId) : state?.projectPendingMutations || [];
      const projects = Array.isArray(payload?.data) ? payload.data : Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;
      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects: projects,
          pendingMutations: nextPendingMutations
        }),
        projectsServerPayload: projects,
        projectPendingMutations: nextPendingMutations
      };
    },

    CREATE_PROJECT_OPTIMISTIC: (state, payload) => {
      const nextPendingMutation = normalizePieveProjectPendingMutation({
        payload,
        mutation: 'create'
      });

      if (!nextPendingMutation) {
        return state;
      }

      const currentPendingMutations = Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : [];
      const nextPendingMutations = [...currentPendingMutations.filter((item) => item?.requestId !== nextPendingMutation.requestId), nextPendingMutation];
      const serverProjects = Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;

      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects,
          pendingMutations: nextPendingMutations
        }),
        projectsServerPayload: serverProjects,
        projectPendingMutations: nextPendingMutations
      };
    },

    CREATE_PROJECT_FAIL: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();

      if (!requestId) {
        return state;
      }

      const nextPendingMutations = (Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : []).filter((item) => item?.requestId !== requestId);
      const serverProjects = Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;

      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects,
          pendingMutations: nextPendingMutations
        }),
        projectPendingMutations: nextPendingMutations
      };
    },

    UPDATE_PROJECT_SUCCESS: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const nextPendingMutations = requestId ? (Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : []).filter((item) => item?.requestId !== requestId) : state?.projectPendingMutations || [];
      const projects = Array.isArray(payload?.data) ? payload.data : Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;
      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects: projects,
          pendingMutations: nextPendingMutations
        }),
        projectsServerPayload: projects,
        projectPendingMutations: nextPendingMutations
      };
    },

    UPDATE_PROJECT_OPTIMISTIC: (state, payload) => {
      const nextPendingMutation = normalizePieveProjectPendingMutation({
        payload,
        mutation: 'update'
      });

      if (!nextPendingMutation) {
        return state;
      }

      const currentPendingMutations = Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : [];
      const nextPendingMutations = [...currentPendingMutations.filter((item) => item?.requestId !== nextPendingMutation.requestId), nextPendingMutation];
      const serverProjects = Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;

      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects,
          pendingMutations: nextPendingMutations
        }),
        projectsServerPayload: serverProjects,
        projectPendingMutations: nextPendingMutations
      };
    },

    UPDATE_PROJECT_FAIL: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();

      if (!requestId) {
        return state;
      }

      const nextPendingMutations = (Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : []).filter((item) => item?.requestId !== requestId);
      const serverProjects = Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;

      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects,
          pendingMutations: nextPendingMutations
        }),
        projectPendingMutations: nextPendingMutations
      };
    },

    REMOVE_PROJECT_SUCCESS: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const nextPendingMutations = requestId ? (Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : []).filter((item) => item?.requestId !== requestId) : state?.projectPendingMutations || [];
      const projects = Array.isArray(payload?.data) ? payload.data : Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;
      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects: projects,
          pendingMutations: nextPendingMutations
        }),
        projectsServerPayload: projects,
        projectPendingMutations: nextPendingMutations
      };
    },

    REMOVE_PROJECT_OPTIMISTIC: (state, payload) => {
      const nextPendingMutation = normalizePieveProjectPendingMutation({
        payload,
        mutation: 'remove'
      });

      if (!nextPendingMutation) {
        return state;
      }

      const currentPendingMutations = Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : [];
      const nextPendingMutations = [...currentPendingMutations.filter((item) => item?.requestId !== nextPendingMutation.requestId), nextPendingMutation];
      const serverProjects = Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;

      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects,
          pendingMutations: nextPendingMutations
        }),
        projectsServerPayload: serverProjects,
        projectPendingMutations: nextPendingMutations
      };
    },

    REMOVE_PROJECT_FAIL: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();

      if (!requestId) {
        return state;
      }

      const nextPendingMutations = (Array.isArray(state?.projectPendingMutations) ? state.projectPendingMutations : []).filter((item) => item?.requestId !== requestId);
      const serverProjects = Array.isArray(state?.projectsServerPayload) ? state.projectsServerPayload : state.projects;

      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects,
          pendingMutations: nextPendingMutations
        }),
        projectPendingMutations: nextPendingMutations
      };
    },

    ACTIVATE_PROJECT_SUCCESS: (state, payload) => {
      const projects = Array.isArray(payload?.data) ? payload.data : state.projects;
      return {
        ...state,
        projects: buildVisibleProjectsFromPendingMutations({
          serverProjects: projects,
          pendingMutations: state?.projectPendingMutations
        }),
        projectsServerPayload: projects
      };
    },

    pieveSetThreads: (state, payload) => {
      const nextScopeCompanyId = resolveCollectionScopeCompanyId({
        payload,
        state,
        ownerKey: 'threadsScopeCompanyId'
      });
      const threadListScope = normalizeThreadListScope(payload?.threadListScope, THREAD_LIST_SCOPE_MAIN);
      const hasProjectsPayload = Array.isArray(payload?.projects);
      const nextProjects = hasProjectsPayload ? payload.projects : state.projects || [];
      const nextProjectsById = {};

      for (const project of nextProjects) {
        const projectId = normalizeProjectId(project?.id);

        if (projectId) {
          nextProjectsById[projectId] = project;
        }
      }
      const existingThreads = normalizeThreadItems({
        threads: getScopedExistingArray({
          items: state.threads,
          ownerCompanyId: state?.threadsScopeCompanyId,
          activeCompanyId: nextScopeCompanyId
        })
      });
      const incomingThreads = Array.isArray(payload?.threads) ? payload.threads : [];
      const stabilizedThreads = reconcileHydratedThreads({
        incomingThreads,
        existingThreads,
        existingThreadContent: state.threadContent,
        projectMetaById: nextProjectsById,
        activeProjectId: normalizeProjectId(payload?.activeProjectId)
      });
      const eventsByThreadId = payload?.eventsByThreadId || {};
      const nextScopedThreadContent = buildThreadContentMap({
        threads: stabilizedThreads,
        eventsByThreadId,
        existingThreadContent: state.threadContent || {}
      });
      const nextThreads = replaceThreadsForScope({
        existingThreads,
        incomingThreads: stabilizedThreads,
        threadListScope
      });
      const nextThreadContent = replaceThreadContentItemsForScope({
        existingThreadContent: state.threadContent,
        existingThreads,
        incomingThreadContent: nextScopedThreadContent,
        threadListScope
      });
      const nextMainThreadSectionCursors = threadListScope === THREAD_LIST_SCOPE_MAIN && payload?.sectionCursors && typeof payload.sectionCursors === 'object' ? payload.sectionCursors : state.mainThreadSectionCursors;
      if (nextThreads === state.threads && nextThreadContent === state.threadContent && nextProjects === state.projects && nextScopeCompanyId === state.threadsScopeCompanyId && nextMainThreadSectionCursors === state.mainThreadSectionCursors) {
        return state;
      }

      const nextSidebar = {
        ...state.sidebar,
        workspaceName: getWorkspaceName(filterThreadsForMainScope(nextThreads))
      };

      return {
        ...state,
        sidebar: nextSidebar,
        projects: nextProjects,
        threads: nextThreads,
        threadContent: nextThreadContent,
        threadsScopeCompanyId: nextScopeCompanyId,
        mainThreadSectionCursors: nextMainThreadSectionCursors
      };
    },

    // The catalog list and the `workflow.updated` push. Rows merge into the ones held — see mergeWorkflowSnapshotRows.
    GET_WORKFLOWS_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflows: mergeWorkflowSnapshotRows(state.workflows, payload?.data),
        workflowsScopeCompanyId: String(payload?.meta?.companyId || state?.workflowsScopeCompanyId || '').trim() || null
      };
    },

    // One thread's draft, definition and all. Its own action so a failed read can be told apart from the catalog's.
    GET_THREAD_WORKFLOW_DRAFT_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflows: mergeWorkflowSnapshotRows(state.workflows, payload?.data)
      };
    },

    /* The actions list is fetched per thread and filed in one place, scoped by the thread that owns the
       response — the same shape as threadAppConnectionStatus below, because it answers the same kind of
       question and has the same two-phase source. It is deliberately NOT read off the draft workflow's
       meta: the list exists before a draft does, and planning is where most blockers are found.

       Answering one row returns the whole list, so the write files exactly what the read does. */
    GET_THREAD_ACTIONS_SUCCESS: applyThreadActionsPayload,

    UPDATE_THREAD_ACTIONS_SUCCESS: applyThreadActionsPayload,

    /* The integrator's Pieve chat binding per project, as pieve.resource_thread returns it: upserted by project and
       user, so opening another project adds to the list rather than replacing it. */
    /* A wording write answers with the stored action. It replaces the one the project holds, so the edited row shows
       its new words at once without re-reading the project. A response for another project is dropped. */
    /* A follow-up written from the project page (created or switched), as stored: replaces the one with its id, or joins
       the list, on the open project only. */
    /* The portal write answers with the whole portal, so it lands on the loaded project as-is. A
       response for a project other than the one open is dropped rather than filed onto it. */
    GET_COMPANY_THREADS_SUCCESS: (state, payload) => {
      return {
        ...state,
        companyThreads: Array.isArray(payload?.data) ? payload.data : [],
        companyThreadsScopeCompanyId: String(payload?.meta?.companyId || state?.companyThreadsScopeCompanyId || '').trim() || null
      };
    },

    PROMOTE_THREAD_SUCCESS: (state, payload) => {
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      const threadsById = getThreadsById(state.threads);
      const nextThreads = rows.reduce((threads, row) => {
        const existing = threadsById[String(row?.id || '').trim()];
        return existing ? upsertSortedThread(threads, { ...existing, isCompany: row?.isCompany === true }) : threads;
      }, state.threads);

      return nextThreads === state.threads ? state : { ...state, threads: nextThreads };
    },

    SET_THREAD_MODE_OPTIMISTIC: (state, payload) => {
      // Flip the thread type instantly so the editor toggle responds without waiting for the
      // round-trip. threadId rides in meta; the canonical thread reconciles on _SUCCESS and the
      // prior mode is restored on _FAIL.
      const threadId = String(payload?.meta?.threadId || '').trim();
      const targetMode = String(payload?.targetMode || '').trim();
      const existing = threadId ? getThreadsById(state.threads)?.[threadId] : null;

      if (!existing || !targetMode) {
        return state;
      }

      return {
        ...state,
        threads: upsertSortedThread(state.threads, { ...existing, threadType: targetMode })
      };
    },

    SET_THREAD_MODE_SUCCESS: (state, payload) => {
      // The mode endpoint returns the canonical thread carrying the new threadType.
      // File it by id through the same thread-array upsert used everywhere else so
      // selectors.threadType reflects the new mode without any reshaping.
      const thread = payload?.data?.thread;

      if (!thread || !getThreadId(thread)) {
        return state;
      }

      return {
        ...state,
        threads: upsertSortedThread(state.threads, thread)
      };
    },

    SET_THREAD_MODE_FAIL: (state, payload) => {
      // The switch was rejected (e.g. a run started): restore the mode captured in meta so the
      // optimistic flip does not strand the toggle in the wrong state.
      const threadId = String(payload?.meta?.threadId || '').trim();
      const previousMode = String(payload?.meta?.previousMode || '').trim();
      const existing = threadId ? getThreadsById(state.threads)?.[threadId] : null;

      if (!existing || !previousMode) {
        return state;
      }

      return {
        ...state,
        threads: upsertSortedThread(state.threads, { ...existing, threadType: previousMode })
      };
    },

    GET_THREAD_APP_CONNECTION_STATUS_SUCCESS: (state, payload) => {
      return {
        ...state,
        threadAppConnectionStatus: Array.isArray(payload?.data) ? payload.data : [],
        threadAppConnectionStatusThreadId: String(payload?.meta?.threadId || '').trim() || null
      };
    },

    GET_COMPANY_ENVIRONMENTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        companyEnvironments: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    /* The created row is appended as the server returned it. It carries no `isPredefined` and no
       usage counts — the seeded read adds those — so the selector derives both rather than trusting
       the field, which is what keeps a freshly created environment and a re-read one identical. */
    POST_COMPANY_ENVIRONMENT_SUCCESS: (state, payload) => {
      const createdEnvironment = payload?.data;

      if (!createdEnvironment?.id) {
        return state;
      }

      return {
        ...state,
        companyEnvironments: [...(state?.companyEnvironments || []), createdEnvironment]
      };
    },

    PUT_COMPANY_ENVIRONMENT_SUCCESS: (state, payload) => {
      const updatedEnvironment = payload?.data;

      if (!updatedEnvironment?.id) {
        return state;
      }

      return {
        ...state,
        companyEnvironments: (state?.companyEnvironments || []).map((environment) => (environment?.id === updatedEnvironment.id ? { ...environment, ...updatedEnvironment } : environment))
      };
    },

    // The delete response carries only the id, which arrives through request `meta`.
    DELETE_COMPANY_ENVIRONMENT_SUCCESS: (state, payload) => {
      const environmentId = String(payload?.meta?.environmentId || '').trim();

      if (!environmentId) {
        return state;
      }

      return {
        ...state,
        companyEnvironments: (state?.companyEnvironments || []).filter((environment) => environment?.id !== environmentId)
      };
    },

    GET_WORKFLOW_CONNECTIONS_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflowConnections: Array.isArray(payload?.data) ? payload.data : [],
        workflowConnectionsWorkflowId: String(payload?.meta?.workflowId || '').trim() || null
      };
    },

    GET_WORKFLOW_WEBHOOK_SAMPLES_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflowWebhookSamples: Array.isArray(payload?.data) ? payload.data : [],
        workflowWebhookSamplesWorkflowId: String(payload?.meta?.workflowId || '').trim() || null
      };
    },

    UPDATE_WORKFLOW_OPTIMISTIC: (state, payload) => {
      // Only the activation toggle is applied optimistically; name/description edits wait
      // for the server round-trip. workflowId rides in meta (id is in the URL).
      const workflowId = String(payload?.meta?.workflowId || '').trim();

      if (!workflowId || typeof payload?.active !== 'boolean') {
        return state;
      }

      const exists = (state.workflows || []).some((item) => String(item?.workflowId || '').trim() === workflowId);

      if (!exists) {
        return state;
      }

      return {
        ...state,
        workflows: mergeByProp('workflowId', state.workflows, [{ workflowId }], (existing) => ({ ...existing, active: payload.active }), { keepPosition: true })
      };
    },

    // The workflow PUT response only carries snapshot columns, not the parsed definition or the linked
    // schedule/webhook/trigger facts; merging keeps them.
    UPDATE_WORKFLOW_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflows: mergeWorkflowSnapshotRows(state.workflows, payload?.data)
      };
    },

    GET_WORKFLOW_SUCCESS: (state, payload) => {
      const workflow = payload?.data?.workflow;

      if (!workflow?.id) {
        return state;
      }

      return {
        ...state,
        workflows: mergeWorkflowSnapshotRows(state.workflows, [workflow]),
        workflowsScopeCompanyId: String(payload?.meta?.companyId || state?.workflowsScopeCompanyId || '').trim() || null
      };
    },

    /* A step edit in flight is an overlay on the confirmed draft, applied by the draft selector in request order, so a
       refetch or an older settlement landing mid-typing never shows an older value over a newer one. Settling one
       removes only that overlay; success also replaces the confirmed draft with the one the server stored, and a
       refused write simply drops its overlay, which puts the confirmed value back. */
    UPDATE_WORKFLOW_STEP_OPTIMISTIC: (state, payload) => {
      const requestId = payload?.meta?.requestId;
      const workflowId = String(payload?.meta?.workflowId || '').trim();
      const stepId = String(payload?.meta?.stepId || '').trim();

      if (!requestId || !workflowId || !stepId || !payload?.patch) {
        return state;
      }

      return { ...state, workflowStepPendingEdits: [...state.workflowStepPendingEdits, { requestId, workflowId, stepId, patch: payload.patch }] };
    },

    UPDATE_WORKFLOW_STEP_SUCCESS: (state, payload) => {
      const requestId = payload?.meta?.requestId;
      const workflow = payload?.data?.workflow;

      return {
        ...state,
        workflowStepPendingEdits: state.workflowStepPendingEdits.filter((edit) => edit.requestId !== requestId),
        workflows: workflow?.id ? mergeWorkflowSnapshotRows(state.workflows, [workflow]) : state.workflows
      };
    },

    UPDATE_WORKFLOW_STEP_FAIL: (state, payload) => {
      const requestId = payload?.meta?.requestId;

      return { ...state, workflowStepPendingEdits: state.workflowStepPendingEdits.filter((edit) => edit.requestId !== requestId) };
    },

    /* `pieve.resource_thread` rows for the steps of a workflow, filed as stored (keyed
       `${workflowId}:${stepId}`; selectors turn the key back into a step). An unsettled ask is a
       row with no threadId — the create is idempotent, so there is no stale settlement to defend
       against. */
    GET_WORKFLOW_STEP_TASKS_SUCCESS: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();

      if (!workflowId) {
        return state;
      }

      const rows = Array.isArray(payload?.data) ? payload.data : [];
      const servedKeys = new Set(rows.map((row) => row?.resourcePrimaryKey));
      /* Served rows replace this workflow's — a merge would keep bindings for deleted steps.
         Unsettled asks survive, being news the server does not have. */
      const kept = (state?.workflowStepTasks || []).filter((task) => {
        const key = String(task?.resourcePrimaryKey || '');

        return !key.startsWith(`${workflowId}:`) || (!task?.threadId && !servedKeys.has(key));
      });

      return { ...state, workflowStepTasks: [...kept, ...rows] };
    },
    POST_WORKFLOW_STEP_THREAD_OPTIMISTIC: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();
      const stepId = String(payload?.meta?.stepId || '').trim();
      const key = workflowId && stepId ? `${workflowId}:${stepId}` : '';
      const tasks = state?.workflowStepTasks || [];

      if (!key || tasks.some((task) => task?.resourcePrimaryKey === key)) {
        return state;
      }

      return { ...state, workflowStepTasks: [...tasks, { resourcePrimaryKey: key, threadId: null }] };
    },
    POST_WORKFLOW_STEP_THREAD_SUCCESS: (state, payload) => {
      const row = payload?.data?.[0];

      if (!row?.resourcePrimaryKey) {
        return state;
      }

      return { ...state, workflowStepTasks: [...(state?.workflowStepTasks || []).filter((task) => task?.resourcePrimaryKey !== row.resourcePrimaryKey), row] };
    },
    POST_WORKFLOW_TEST_THREAD_SUCCESS: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();
      const threadId = String(payload?.data?.[0]?.threadId || '').trim();

      if (!workflowId || !threadId) {
        return state;
      }

      return { ...state, workflowTestThreadIdByWorkflowId: { ...state?.workflowTestThreadIdByWorkflowId, [workflowId]: threadId } };
    },
    POST_WORKFLOW_STEP_THREAD_FAIL: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();
      const stepId = String(payload?.meta?.stepId || '').trim();

      if (!workflowId || !stepId) {
        return state;
      }

      const key = `${workflowId}:${stepId}`;
      const tasks = state?.workflowStepTasks || [];
      const remaining = tasks.filter((task) => task?.resourcePrimaryKey !== key || task?.threadId);

      return remaining.length === tasks.length ? state : { ...state, workflowStepTasks: remaining };
    },
    GET_WORKFLOW_STEP_OUTPUT_SUCCESS: (state, payload) => {
      const stepId = payload?.meta?.stepId;

      if (!stepId) {
        return state;
      }

      return {
        ...state,
        workflowStepOutputs: mergeByProp('stepId', state.workflowStepOutputs, [{ stepId, data: payload?.data || null }])
      };
    },

    GET_WORKFLOW_VERSIONS_SUCCESS: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();

      if (!workflowId) {
        return state;
      }

      const items = Array.isArray(payload?.data?.items) ? payload.data.items : [];
      const incoming = items.filter(Boolean).map((item) => ({ ...item, workflowId: item?.workflowId || workflowId }));
      const retained = (state?.workflowVersionsList || []).filter((version) => String(version?.workflowId || '').trim() !== workflowId);

      return {
        ...state,
        workflowVersionsList: [...retained, ...incoming]
      };
    },

    WORKFLOW_RUN_SEARCH_STARTED: (state, payload) => ({ ...state, workflowRunSearch: { requestKey: payload.requestKey, requestId: payload.requestId, pending: true, response: null } }),

    SEARCH_WORKFLOW_RUNS_SUCCESS: (state, payload) => {
      if (state.workflowRunSearch?.requestKey !== payload?.meta?.requestKey || state.workflowRunSearch?.requestId !== payload?.meta?.requestId) {
        return state;
      }
      return { ...state, workflowRunSearch: { requestKey: payload.meta.requestKey, requestId: payload.meta.requestId, pending: false, response: payload } };
    },

    SEARCH_WORKFLOW_RUNS_FAIL: (state, payload) => {
      if (state.workflowRunSearch?.requestKey !== payload?.meta?.requestKey || state.workflowRunSearch?.requestId !== payload?.meta?.requestId) {
        return state;
      }
      return { ...state, workflowRunSearch: { requestKey: payload.meta.requestKey, requestId: payload.meta.requestId, pending: false, response: payload } };
    },

    GET_WORKFLOW_RUNS_SUCCESS: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();

      if (!workflowId) {
        return state;
      }

      // Rows land unchanged: the query already selects workflow_run.workflow_id on every row, so the
      // old `run.workflowId || workflowId` per-row fallback could never fire (2026-08-19).
      const incoming = Array.isArray(payload?.data) ? payload.data.filter(Boolean) : [];
      const retained = (state?.workflowRunList || []).filter((run) => String(run?.workflowId || '').trim() !== workflowId);

      return {
        ...state,
        workflowRunList: [...retained, ...incoming],
        workflowRunListPaging: { ...state.workflowRunListPaging, [workflowId]: { nextCursor: payload.meta.nextCursor || null, totalCount: payload.meta.totalCount ?? null, environmentIds: payload.meta.environmentIds } }
      };
    },

    GET_OLDER_WORKFLOW_RUNS_SUCCESS: (state, payload) => {
      const { workflowId, cursor, nextCursor } = payload.meta;
      const paging = state.workflowRunListPaging[workflowId];

      // A first-page reload (date filter change, run event) since this request replaced the list it was paging.
      if (!paging || paging.nextCursor !== cursor) {
        return state;
      }

      const held = new Set(state.workflowRunList.map((run) => run.id));

      return {
        ...state,
        workflowRunList: [...state.workflowRunList, ...payload.data.filter((run) => !held.has(run.id))],
        workflowRunListPaging: { ...state.workflowRunListPaging, [workflowId]: { ...paging, nextCursor: nextCursor || null } }
      };
    },

    RUN_WORKFLOW_OPTIMISTIC: (state, payload) => {
      // Insert a queued run the instant the button is clicked so the run list — the single
      // source of truth for the "Run workflow" busy state — is populated before the follow-up
      // GET lands. Without this the button flickers loading -> idle -> loading in the handoff.
      const workflowId = String(payload?.workflowId || '').trim();
      const threadId = String(payload?.meta?.threadId || '').trim();
      const requestId = String(payload?.meta?.requestId || '').trim();

      if (!workflowId || !threadId || !requestId) {
        return state;
      }

      const queuedAt = new Date(Number(payload?.meta?.localChangeAt) || Date.now()).toISOString();
      const optimisticRun = { id: requestId, workflowId, threadId, status: 'queued', createdAt: queuedAt, meta: { requestId, queuedAt } };
      const retained = (state?.workflowRunList || []).filter((run) => String(run?.meta?.requestId || '').trim() !== requestId);

      return {
        ...state,
        workflowRunList: [...retained, optimisticRun]
      };
    },

    RUN_WORKFLOW_FAIL: (state, payload) => {
      // The run request never reached the backend, and no workflowRun event fires to trigger a
      // reconciling GET, so drop the optimistic run here or it lingers as active for 2 minutes.
      const requestId = String(payload?.meta?.requestId || '').trim();

      if (!requestId) {
        return state;
      }

      return {
        ...state,
        workflowRunList: (state?.workflowRunList || []).filter((run) => String(run?.meta?.requestId || '').trim() !== requestId)
      };
    },

    GET_WORKFLOW_RUN_TIME_MACHINE_SUCCESS: (state, payload) => {
      const workflowRunId = String(payload?.meta?.workflowRunId || '').trim();

      if (!workflowRunId) {
        return state;
      }

      const data = payload?.data || {};
      const incoming = {
        workflowRunId,
        run: data?.run || null,
        payload: data?.payload || null,
        workflow: data?.workflow || null,
        artifact: data?.artifact || null,
        warning: data?.warning || null
      };
      const retained = (state?.workflowRunTimeMachineList || []).filter((entry) => String(entry?.workflowRunId || '').trim() !== workflowRunId);

      return {
        ...state,
        workflowRunTimeMachineList: [...retained, incoming]
      };
    },

    GET_WORKFLOW_RUN_OPERATIONS_SUCCESS: (state, payload) => {
      const workflowRunId = String(payload?.meta?.workflowRunId || '').trim();

      if (!workflowRunId) {
        return state;
      }

      const incoming = Array.isArray(payload?.data) ? payload.data : [];
      const retained = state.workflowRunOperationsList.filter((operation) => operation.runId !== workflowRunId);

      return {
        ...state,
        workflowRunOperationsList: [...retained, ...incoming]
      };
    },

    GET_WORKFLOW_OPERATIONS_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflowOperationsList: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_WORKFLOW_RECENT_ACTIVITY_CHART_DAILY_COUNTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflowRecentActivityChartDailyCounts: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_WORKFLOW_RUN_DAILY_COUNTS_SUCCESS: (state, payload) => {
      const threadId = String(payload?.meta?.threadId || payload?.data?.meta?.threadId || '').trim();
      const workflowId = String(payload?.meta?.workflowId || '').trim();

      if (!threadId) {
        return state;
      }

      const items = Array.isArray(payload?.data) ? payload.data : [];
      const incoming = items.filter(Boolean).map((row) => ({ ...row, threadId: row?.threadId || threadId, workflowId: row?.workflowId || workflowId }));
      const retained = (state?.workflowRunDailyCountsList || []).filter((row) => String(row?.threadId || '').trim() !== threadId);

      return {
        ...state,
        workflowRunDailyCountsList: [...retained, ...incoming]
      };
    },

    pieveSetTriggerItems: (state, payload) => {
      const nextScopeCompanyId = resolveCollectionScopeCompanyId({
        payload,
        state,
        ownerKey: 'triggerItemsScopeCompanyId'
      });
      const previousTriggerItems = getScopedExistingArray({
        items: state?.triggerItems,
        ownerCompanyId: state?.triggerItemsScopeCompanyId,
        activeCompanyId: nextScopeCompanyId
      });
      const incomingItems = Array.isArray(payload?.triggerItems) ? payload.triggerItems : [];

      if (nextScopeCompanyId === state?.triggerItemsScopeCompanyId && previousTriggerItems.length === incomingItems.length && previousTriggerItems.every((item, i) => normalizeTriggerItemId(item?.id) === normalizeTriggerItemId(incomingItems[i]?.id))) {
        return state;
      }

      return {
        ...state,
        triggerItems: incomingItems,
        triggerItemsScopeCompanyId: nextScopeCompanyId
      };
    },

    pieveSetThreadEvents: (state, payload) => {
      const threadId = payload?.threadId;
      const events = Array.isArray(payload?.events) ? payload.events : [];
      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!threadId || !ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const nextThreadContent = ensuredThread.threadContent;

      const normalizedRunState = String(existingThread?.runState?.state || '')
        .trim()
        .toLowerCase();
      const shouldPreserveStreamingProgress = normalizedRunState && !NON_RUNNING_RUN_STATES.has(normalizedRunState);
      const nextEvents = mergeHydratedEvents({
        existingEvents: existingThread?.activity || [],
        incomingEvents: events,
        preserveProvisionalEvents: Boolean(shouldPreserveStreamingProgress)
      });

      return {
        ...state,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          activity: sortEvents(nextEvents),
          /* Marks the difference between "no events yet fetched" and "fetched, genuinely
             empty" — the composer placeholder suppresses its new-thread prompt until this
             flips, so an existing thread never flashes the prompt before 'Reply...'. */
          eventsHydrated: true
        })
      };
    },

    pieveSetThreadAppConnections: (state, payload) => {
      const threadId = payload?.threadId;
      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!threadId || !ensuredThread) {
        return state;
      }

      return {
        ...state,
        threadContent: upsertThreadContentItem(ensuredThread.threadContent, {
          ...ensuredThread.threadContentItem,
          threadId,
          appConnections: Array.isArray(payload?.appConnections) ? payload.appConnections : []
        })
      };
    },

    SET_THREAD_APP_CONNECTION_SUCCESS: (state, payload) => {
      return applyThreadAppConnectionSelectionSuccess({
        state,
        payload
      });
    },

    CLEAR_THREAD_APP_CONNECTION_SUCCESS: (state, payload) => {
      return applyThreadAppConnectionClearSuccess({
        state,
        payload
      });
    },

    pieveArchiveThreadOptimistic: (state, payload) => {
      const threadId = payload?.threadId;

      if (!threadId) {
        return state;
      }

      const existingThreadMeta = getThreadsById(state.threads)?.[threadId] || null;

      if (!existingThreadMeta && !getThreadContentItemByThreadId(state.threadContent, threadId)) {
        return state;
      }

      const nextThreads = removeThread(state.threads, threadId);
      const nextThreadContent = removeThreadContentItem(state.threadContent, threadId);

      return {
        ...state,
        threads: nextThreads,
        threadContent: nextThreadContent
      };
    },

    DUPLICATE_THREAD_SUCCESS: (state, payload) => {
      const thread = payload?.data?.thread || payload?.thread || null;
      const threadId = String(thread?.threadId || thread?.id || '').trim();
      if (!threadId || !thread) {
        return state;
      }
      const normalized = { ...thread, id: threadId };
      return {
        ...state,
        threads: [normalized, ...(state.threads || [])]
      };
    },

    ARCHIVE_THREAD_OPTIMISTIC: (state, payload) => {
      const threadId = String(payload?.resourceId || '').trim();
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();

      if (!threadId || !requestId) {
        return state;
      }

      const existingThreadMeta = getThreadsById(state.threads)?.[threadId] || null;
      const existingThreadContent = getThreadContentItemByThreadId(state.threadContent, threadId) || null;

      if (!existingThreadMeta && !existingThreadContent) {
        return state;
      }

      const nextThreads = removeThread(state.threads, threadId);
      const nextThreadContent = removeThreadContentItem(state.threadContent, threadId);
      const nextArchivedThreadSnapshotByThreadId = {
        ...(state?.archivedThreadSnapshotByThreadId || {}),
        [threadId]: {
          threadMeta: existingThreadMeta,
          threadContent: existingThreadContent
        }
      };

      return {
        ...state,
        threads: nextThreads,
        threadContent: nextThreadContent,
        archivedThreadSnapshotByThreadId: nextArchivedThreadSnapshotByThreadId,
        archivedThreadPendingRequestIdByThreadId: {
          ...(state?.archivedThreadPendingRequestIdByThreadId || {}),
          [threadId]: requestId
        }
      };
    },

    ARCHIVE_THREAD_SUCCESS: (state, payload) => {
      const threadId = String(payload?.resourceId || payload?.meta?.threadId || '').trim();
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();

      if (!threadId || !requestId || state?.archivedThreadPendingRequestIdByThreadId?.[threadId] !== requestId) {
        return state;
      }

      const nextArchivedThreadSnapshotByThreadId = { ...(state?.archivedThreadSnapshotByThreadId || {}) };
      const nextArchivedThreadPendingRequestIdByThreadId = { ...(state?.archivedThreadPendingRequestIdByThreadId || {}) };
      delete nextArchivedThreadSnapshotByThreadId[threadId];
      delete nextArchivedThreadPendingRequestIdByThreadId[threadId];

      return {
        ...state,
        archivedThreadSnapshotByThreadId: nextArchivedThreadSnapshotByThreadId,
        archivedThreadPendingRequestIdByThreadId: nextArchivedThreadPendingRequestIdByThreadId
      };
    },

    ARCHIVE_THREAD_FAIL: (state, payload) => {
      return applyArchiveThreadFail({
        state,
        payload
      });
    },

    pieveRestoreArchivedThread: (state, payload) => {
      const threadMeta = payload?.threadMeta;
      const threadContent = payload?.threadContent;
      const threadId = threadMeta?.id || null;

      if (!threadId || !threadMeta) {
        return state;
      }

      let nextThreadContent = buildThreadContentItems(state.threadContent);

      if (threadContent) {
        nextThreadContent = upsertThreadContentItem(nextThreadContent, {
          ...(threadContent || {}),
          threadId
        });
      }

      return {
        ...state,
        threads: upsertSortedThread(state.threads, threadMeta),
        threadContent: nextThreadContent
      };
    },

    pieveUpsertThreadOptimistic: (state, payload) => {
      const incomingThread = payload?.thread;
      const threadId = getThreadId(incomingThread);

      if (!threadId || !incomingThread || typeof incomingThread !== 'object') {
        return state;
      }

      const existingMeta = getThreadsById(state.threads)?.[threadId] || {};
      const existingContent = getThreadContentItemByThreadId(state.threadContent, threadId);
      const nowIso = new Date().toISOString();
      const shouldPreserveExistingMetadata = isIncomingThreadMetadataStale({
        existingThread: existingMeta,
        incomingThread
      });
      const createdAt = getFirstValidThreadTimestamp(shouldPreserveExistingMetadata ? [existingMeta?.createdAt, existingMeta?.created_at, incomingThread?.createdAt, incomingThread?.created_at, existingMeta?.updatedAt, existingMeta?.updated_at, incomingThread?.updatedAt, incomingThread?.updated_at, nowIso] : [incomingThread?.createdAt, incomingThread?.created_at, existingMeta?.createdAt, existingMeta?.created_at, incomingThread?.updatedAt, incomingThread?.updated_at, existingMeta?.updatedAt, existingMeta?.updated_at, nowIso]) || nowIso;
      const updatedAt = getFirstValidThreadTimestamp(shouldPreserveExistingMetadata ? [existingMeta?.updatedAt, existingMeta?.updated_at, incomingThread?.updatedAt, incomingThread?.updated_at, createdAt] : [incomingThread?.updatedAt, incomingThread?.updated_at, existingMeta?.updatedAt, existingMeta?.updated_at, createdAt]) || createdAt;
      const projectId = getThreadProjectId(incomingThread) || getThreadProjectId(existingMeta);
      const parentThreadId = getThreadParentId(incomingThread) || getThreadParentId(existingMeta);
      const mergedThreadMeta = shouldPreserveExistingMetadata ? { ...incomingThread, ...existingMeta } : { ...existingMeta, ...incomingThread };
      const existingLastOpenedAt = existingMeta?.lastOpenedAt || existingMeta?.last_opened_at || null;
      const incomingLastOpenedAt = incomingThread?.lastOpenedAt || incomingThread?.last_opened_at || null;
      const existingLastOpenedAtMs = toThreadTimestampMs(existingLastOpenedAt);
      const incomingLastOpenedAtMs = toThreadTimestampMs(incomingLastOpenedAt);
      const resolvedLastOpenedAt = Number.isFinite(existingLastOpenedAtMs) && Number.isFinite(incomingLastOpenedAtMs) ? (existingLastOpenedAtMs >= incomingLastOpenedAtMs ? existingLastOpenedAt : incomingLastOpenedAt) : existingLastOpenedAt || incomingLastOpenedAt || null;
      const existingLatestAssistantResponseAt = existingMeta?.latestAssistantResponseAt || existingMeta?.latest_assistant_response_at || null;
      const incomingLatestAssistantResponseAt = incomingThread?.latestAssistantResponseAt || incomingThread?.latest_assistant_response_at || null;
      const existingLatestAssistantResponseAtMs = toThreadTimestampMs(existingLatestAssistantResponseAt);
      const incomingLatestAssistantResponseAtMs = toThreadTimestampMs(incomingLatestAssistantResponseAt);
      const resolvedLatestAssistantResponseAt = Number.isFinite(existingLatestAssistantResponseAtMs) && Number.isFinite(incomingLatestAssistantResponseAtMs) ? (existingLatestAssistantResponseAtMs >= incomingLatestAssistantResponseAtMs ? existingLatestAssistantResponseAt : incomingLatestAssistantResponseAt) : incomingLatestAssistantResponseAt || existingLatestAssistantResponseAt || null;
      const nextThreadMetaRunState = shouldPreserveExistingRunStateDuringHydration({
        existingRunState: existingMeta?.runState || null,
        incomingRunState: incomingThread?.runState || { state: 'idle', runId: null }
      })
        ? existingMeta?.runState || null
        : incomingThread?.runState || existingMeta?.runState || { state: 'idle', runId: null };
      const nextThreadMeta = {
        ...mergedThreadMeta,
        id: threadId,
        createdAt,
        updatedAt,
        runState: nextThreadMetaRunState,
        optimisticCreatedAt: existingMeta?.optimisticCreatedAt || nowIso,
        lastOpenedAt: resolvedLastOpenedAt,
        latestAssistantResponseAt: resolvedLatestAssistantResponseAt,
        ...(projectId ? { projectId } : {}),
        ...(parentThreadId ? { parentThreadId } : {}),
        ...(projectId
          ? {
              project: {
                ...(existingMeta?.project || {}),
                ...(incomingThread?.project || {}),
                id: projectId
              }
            }
          : {})
      };
      const nextThreadRunState = shouldPreserveExistingRunStateDuringHydration({
        existingRunState: existingContent?.runState || null,
        incomingRunState: nextThreadMeta?.runState || { state: 'idle', runId: null }
      })
        ? existingContent?.runState || null
        : nextThreadMeta?.runState || { state: 'idle', runId: null };
      const nextThreadContent = upsertThreadContentItem(
        state.threadContent,
        buildThreadContentShell({
          thread: nextThreadMeta,
          threadId,
          existingContent,
          runState: nextThreadRunState
        })
      );

      const nextState = {
        ...state,
        threads: upsertSortedThread(state.threads, nextThreadMeta),
        threadContent: nextThreadContent
      };

      return applyCommittedSidebarPlacement({ state: nextState, commit: payload?.sidebarOrganizationCommit, settledAt: payload?.sidebarOrganizationCommittedAt });
    },

    pieveSetThreadUpdatedAt: (state, payload) => {
      const threadId = typeof payload?.threadId === 'string' ? payload.threadId.trim() : '';
      const updatedAt = payload?.updatedAt;
      const updatedAtMs = typeof updatedAt === 'string' ? Date.parse(updatedAt) : NaN;
      const existingThread = getThreadsById(state.threads)?.[threadId] || null;

      if (!threadId || !Number.isFinite(updatedAtMs) || !existingThread) {
        return state;
      }

      const existingUpdatedAtMs = typeof existingThread.updatedAt === 'string' ? Date.parse(existingThread.updatedAt) : NaN;

      if (Number.isFinite(existingUpdatedAtMs) && existingUpdatedAtMs > updatedAtMs) {
        return state;
      }

      return {
        ...state,
        threads: upsertSortedThread(state.threads, {
          ...existingThread,
          updatedAt
        })
      };
    },

    pieveUpsertThreadEvent: (state, payload) => {
      const threadId = payload?.threadId;
      const event = payload?.event;

      if (!threadId || !event?.id) {
        return state;
      }

      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const nextThreadContent = ensuredThread.threadContent;

      const prevActivity = existingThread.activity || [];
      const eventSeq = getEventSeq(event);
      const idxById = prevActivity.findIndex((item) => item?.id === event.id);
      const idxBySeq = eventSeq === null ? -1 : prevActivity.findIndex((item) => getEventSeq(item) === eventSeq && !isSiblingArtifactPreview({ existingEvent: item, incomingEvent: event }));
      const targetIdx = idxById >= 0 ? idxById : idxBySeq;

      if (targetIdx >= 0) {
        const existingEvent = prevActivity[targetIdx] || {};
        const mergedEventCandidate = {
          ...existingEvent,
          ...event,
          id: event.id
        };
        const mergedEventWithStableLines = shouldPreserveExistingEventLines({
          existingEvent,
          incomingEvent: event,
          assistantBubbleOnly: true
        })
          ? {
              ...mergedEventCandidate,
              lines: existingEvent.lines,
              ...(existingEvent.assistantTextFinal === true ? { assistantTextFinal: true } : {})
            }
          : mergedEventCandidate;
        const mergedEvent = shouldPreserveExistingInteractionResponseSummary({
          existingEvent,
          incomingEvent: mergedEventWithStableLines
        })
          ? preserveExistingInteractionResponsePresentation({
              existingEvent,
              incomingEvent: mergedEventWithStableLines
            })
          : mergedEventWithStableLines;

        const nextActivity = prevActivity.map((item, index) => (index === targetIdx ? mergedEvent : item));

        if (isDeepEqual(existingEvent, mergedEvent) && nextActivity.length === prevActivity.length) {
          return state;
        }

        return {
          ...state,
          threadContent: upsertThreadContentItem(nextThreadContent, {
            ...existingThread,
            threadId,
            activity: reconcileToolCallEventsWithToolResults(sortEvents(nextActivity))
          })
        };
      }

      const pendingReplacementIdx = findPendingUserReplacementIndex({
        existingEvents: prevActivity,
        incomingEvent: event
      });

      if (pendingReplacementIdx >= 0) {
        const pendingEvent = prevActivity[pendingReplacementIdx] || {};
        const replacementEvent = {
          ...pendingEvent,
          ...event,
          id: event.id
        };

        if (isDeepEqual(pendingEvent, replacementEvent)) {
          return state;
        }

        return {
          ...state,
          threadContent: upsertThreadContentItem(nextThreadContent, {
            ...existingThread,
            threadId,
            activity: reconcileToolCallEventsWithToolResults(sortEvents(prevActivity.map((item, index) => (index === pendingReplacementIdx ? replacementEvent : item))))
          })
        };
      }

      // A plan run's uploaded reply takes over the live text it already showed on this Mac.
      const nextActivity = prevActivity.concat([event]);

      return {
        ...state,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          activity: reconcileToolCallEventsWithToolResults(sortEvents(nextActivity))
        })
      };
    },

    pieveDeleteThreadEvent: (state, payload) => {
      const threadId = payload?.threadId;
      const eventId = payload?.eventId;

      if (!threadId || !eventId) {
        return state;
      }

      const existingThread = getThreadContentItemByThreadId(state.threadContent, threadId);

      if (!existingThread) {
        return state;
      }

      const prevActivity = existingThread.activity || [];
      const nextActivity = prevActivity.filter((event) => event?.id !== eventId);

      if (nextActivity.length === prevActivity.length) {
        return state;
      }

      return {
        ...state,
        threadContent: upsertThreadContentItem(state.threadContent, {
          ...existingThread,
          threadId,
          activity: nextActivity
        })
      };
    },

    pieveApplyAssistantDelta: (state, payload) => {
      const threadId = payload?.threadId;
      const eventId = payload?.eventId;
      const delta = payload?.delta;

      if (!threadId || !eventId || !delta) {
        return state;
      }

      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const nextThreadContent = ensuredThread.threadContent;

      const prevActivity = existingThread.activity || [];
      const idx = prevActivity.findIndex((e) => e?.id === eventId);

      if (idx < 0) {
        return state;
      }

      const prevEvent = prevActivity[idx];
      const prevText = prevEvent?.lines?.[0]?.text || '';
      const textOffset = payload?.textOffset;

      // Unpositioned deltas retain the legacy v2 contract during independent API/web deploys.
      if (prevEvent?.assistantTextFinal === true || (textOffset !== undefined && (!Number.isSafeInteger(textOffset) || textOffset !== prevText.length))) {
        return state;
      }

      const nextEvent = {
        ...prevEvent,
        lines: [{ ...(prevEvent?.lines?.[0] || {}), text: `${prevText}${String(delta)}` }]
      };

      return {
        ...state,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          activity: prevActivity.map((e, i) => (i === idx ? nextEvent : e))
        })
      };
    },

    RESPOND_APPROVAL_OPTIMISTIC: (state, payload) => {
      const threadId = String(payload?.meta?.threadId || '').trim();
      const approvalId = String(payload?.meta?.approvalId || '').trim();
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const approved = payload?.meta?.approved;
      const remember = payload?.meta?.remember;

      if (!threadId || !approvalId || !requestId) {
        return state;
      }

      const ensured = ensureThreadContentItem({ state, threadId });
      if (!ensured) {
        return state;
      }

      const activity = Array.isArray(ensured.threadContentItem?.activity) ? ensured.threadContentItem.activity : [];
      const decision = approved ? (remember ? 'always_allow' : 'allow') : 'deny';
      const approvalDecisionKey = `${threadId}:${approvalId}`;
      const currentServerDecision = Object.prototype.hasOwnProperty.call(state?.approvalDecisionServerStateByKey || {}, approvalDecisionKey)
        ? state.approvalDecisionServerStateByKey[approvalDecisionKey]
        : (() => {
            const currentApprovalItem = activity.find((item) => (item?.approvalId || item?.id) === approvalId) || null;
            return currentApprovalItem?.decision || null;
          })();

      const nextActivity = activity.map((item) => {
        if ((item?.approvalId || item?.id) === approvalId) {
          return { ...item, decision };
        }
        return item;
      });

      return {
        ...state,
        approvalDecisionServerStateByKey: {
          ...(state?.approvalDecisionServerStateByKey || {}),
          [approvalDecisionKey]: currentServerDecision
        },
        approvalDecisionPendingRequestIdByKey: {
          ...(state?.approvalDecisionPendingRequestIdByKey || {}),
          [approvalDecisionKey]: requestId
        },
        threadContent: {
          ...ensured.threadContent,
          [threadId]: {
            ...ensured.threadContentItem,
            activity: nextActivity
          }
        }
      };
    },

    RESPOND_APPROVAL_SUCCESS: (state, payload) => {
      const threadId = String(payload?.meta?.threadId || '').trim();
      const approvalId = String(payload?.meta?.approvalId || '').trim();
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const approved = payload?.meta?.approved;
      const remember = payload?.meta?.remember;
      const approvalDecisionKey = `${threadId}:${approvalId}`;

      if (!threadId || !approvalId || !requestId || state?.approvalDecisionPendingRequestIdByKey?.[approvalDecisionKey] !== requestId) {
        return state;
      }

      const nextApprovalDecisionPendingRequestIdByKey = { ...(state?.approvalDecisionPendingRequestIdByKey || {}) };
      delete nextApprovalDecisionPendingRequestIdByKey[approvalDecisionKey];

      return {
        ...state,
        approvalDecisionServerStateByKey: {
          ...(state?.approvalDecisionServerStateByKey || {}),
          [approvalDecisionKey]: approved ? (remember ? 'always_allow' : 'allow') : 'deny'
        },
        approvalDecisionPendingRequestIdByKey: nextApprovalDecisionPendingRequestIdByKey
      };
    },

    RESPOND_APPROVAL_FAIL: (state, payload) => {
      const threadId = String(payload?.meta?.threadId || '').trim();
      const approvalId = String(payload?.meta?.approvalId || '').trim();
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();
      const approvalDecisionKey = `${threadId}:${approvalId}`;

      if (!threadId || !approvalId || !requestId || state?.approvalDecisionPendingRequestIdByKey?.[approvalDecisionKey] !== requestId) {
        return state;
      }

      const ensured = ensureThreadContentItem({ state, threadId });
      if (!ensured) {
        return state;
      }

      const activity = Array.isArray(ensured.threadContentItem?.activity) ? ensured.threadContentItem.activity : [];
      const serverDecision = Object.prototype.hasOwnProperty.call(state?.approvalDecisionServerStateByKey || {}, approvalDecisionKey) ? state.approvalDecisionServerStateByKey[approvalDecisionKey] : null;

      const nextActivity = activity.map((item) => {
        if ((item?.approvalId || item?.id) === approvalId) {
          if (serverDecision) {
            return {
              ...item,
              decision: serverDecision
            };
          }

          if (item?.decision) {
            const rest = { ...item };
            delete rest.decision;
            return rest;
          }
        }
        return item;
      });

      const nextApprovalDecisionPendingRequestIdByKey = { ...(state?.approvalDecisionPendingRequestIdByKey || {}) };
      delete nextApprovalDecisionPendingRequestIdByKey[approvalDecisionKey];

      return {
        ...state,
        approvalDecisionPendingRequestIdByKey: nextApprovalDecisionPendingRequestIdByKey,
        threadContent: {
          ...ensured.threadContent,
          [threadId]: {
            ...ensured.threadContentItem,
            activity: nextActivity
          }
        }
      };
    },

    RESPOND_CONNECTION_FAIL: rollbackConnectionOutcome,

    RESPOND_CONNECTION_OPTIMISTIC: (state, payload) => {
      const threadId = String(payload?.meta?.threadId || '').trim();
      const interactionId = String(payload?.meta?.interactionId || '').trim();
      const outcome = String(payload?.outcome || '').trim();

      if (!threadId || !interactionId || !outcome) {
        return state;
      }

      const ensured = ensureThreadContentItem({ state, threadId });
      if (!ensured) {
        return state;
      }

      const activity = Array.isArray(ensured.threadContentItem?.activity) ? ensured.threadContentItem.activity : [];

      const nextActivity = activity.map((item) => {
        if ((item?.interactionId || item?.id) === interactionId) {
          return { ...item, outcome, respondedAt: new Date().toISOString() };
        }
        return item;
      });

      return {
        ...state,
        threadContent: {
          ...ensured.threadContent,
          [threadId]: {
            ...ensured.threadContentItem,
            activity: nextActivity
          }
        }
      };
    },

    // The outcome arrives after the run stream closes. Apply it before the metadata
    // refetch, which can still be deduplicated against a pre-verdict snapshot.
    pieveApplyThreadRunOutcome: (state, payload) => {
      const threadId = payload?.threadId;
      const runId = payload?.runId || null;
      const completionOutcome = payload?.outcome || null;
      const completionSummary = payload?.summary || null;

      if (!threadId || !completionOutcome) {
        return state;
      }

      const existingThread = getThreadsById(state.threads)?.[threadId];

      if (!existingThread?.runState || (runId && existingThread.runState.runId && existingThread.runState.runId !== runId)) {
        return state;
      }

      return {
        ...state,
        threads: upsertSortedThread(state.threads, {
          ...existingThread,
          runState: {
            ...existingThread.runState,
            meta: {
              ...(existingThread.runState.meta || {}),
              completionOutcome,
              ...(completionSummary ? { completionSummary } : {})
            }
          }
        })
      };
    },

    pieveSetThreadRunState: (state, payload) => {
      const threadId = payload?.threadId;
      const runId = payload?.runId || null;
      const runState = payload?.state || 'idle';
      const meta = payload?.meta || null;
      const blockerCode = payload?.blockerCode || null;
      const blockerReason = payload?.blockerReason || null;
      const blockerMessage = payload?.blockerMessage || null;
      const hasStartedAt = Object.prototype.hasOwnProperty.call(payload || {}, 'startedAt');
      const hasCompletedAt = Object.prototype.hasOwnProperty.call(payload || {}, 'completedAt');
      const hasDurationMs = Object.prototype.hasOwnProperty.call(payload || {}, 'durationMs');
      const hasActiveDurationMs = Object.prototype.hasOwnProperty.call(payload || {}, 'activeDurationMs');
      const hasBlockedDurationMs = Object.prototype.hasOwnProperty.call(payload || {}, 'blockedDurationMs');
      const parseDuration = (value) => {
        const parsed = Number(value);

        if (!Number.isFinite(parsed) || parsed < 0) {
          return null;
        }

        return parsed;
      };

      if (!threadId) {
        return state;
      }

      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const existingMeta = ensuredThread.threadMeta || null;
      const nextThreadContent = ensuredThread.threadContent;
      const existingRunState = existingThread?.runState || {};
      // An optimistic start knows its lane before thread metadata arrives. Keep it only for the same run.
      const composerLane = payload?.composerLane !== undefined ? payload.composerLane : existingRunState.runId === runId ? existingRunState.composerLane || null : null;
      const startedAt = hasStartedAt ? payload?.startedAt || null : existingRunState?.startedAt || null;
      const completedAt = hasCompletedAt ? payload?.completedAt || null : existingRunState?.completedAt || null;
      const durationMs = hasDurationMs ? parseDuration(payload?.durationMs) : parseDuration(existingRunState?.durationMs);
      const activeDurationMs = hasActiveDurationMs ? parseDuration(payload?.activeDurationMs) : parseDuration(existingRunState?.activeDurationMs);
      const blockedDurationMs = hasBlockedDurationMs ? (parseDuration(payload?.blockedDurationMs) ?? 0) : (parseDuration(existingRunState?.blockedDurationMs) ?? 0);

      const nextThreads = existingMeta
        ? upsertSortedThread(state.threads, {
            ...existingMeta,
            runState: {
              ...(existingMeta?.runState || {}),
              state: runState,
              runId,
              startedAt,
              completedAt,
              durationMs,
              activeDurationMs,
              blockedDurationMs
            }
          })
        : state.threads;

      const isRunTerminated = NON_RUNNING_RUN_STATES.has(String(runState).trim().toLowerCase());

      return {
        ...state,
        threads: nextThreads,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          runState: {
            state: runState,
            runId,
            composerLane,
            meta,
            blockerCode,
            blockerReason,
            blockerMessage,
            startedAt,
            completedAt,
            durationMs,
            activeDurationMs,
            blockedDurationMs
          },
          webSearch: isRunTerminated ? null : existingThread.webSearch || null
        })
      };
    },

    pieveSetThreadWebSearchState: (state, payload) => {
      const threadId = payload?.threadId;
      const status = String(payload?.status || '').trim() || null;

      if (!threadId || !status) {
        return state;
      }

      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const nextThreadContent = ensuredThread.threadContent;

      const callsCompleted = Number.isFinite(Number(payload?.callsCompleted)) ? Number(payload.callsCompleted) : null;
      const maxCalls = Number.isFinite(Number(payload?.maxCalls)) ? Number(payload.maxCalls) : null;

      return {
        ...state,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          webSearch: { status, callsCompleted, maxCalls }
        })
      };
    },

    pieveSetThreadDiffCard: (state, payload) => {
      const threadId = payload?.threadId;
      const diffCard = payload?.diffCard || null;

      if (!threadId) {
        return state;
      }

      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const nextThreadContent = ensuredThread.threadContent;

      return {
        ...state,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          diffCard
        })
      };
    },

    pieveSetThreadContextWindowUsage: (state, payload) => {
      const threadId = payload?.threadId;
      const contextWindowUsage = payload?.contextWindowUsage || null;

      if (!threadId) {
        return state;
      }

      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const nextThreadContent = ensuredThread.threadContent;

      if (isDeepEqual(existingThread?.contextWindowUsage || null, contextWindowUsage)) {
        return state;
      }

      return {
        ...state,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          contextWindowUsage
        })
      };
    },

    pieveSetThreadFilePreview: (state, payload) => {
      const threadId = payload?.threadId;
      const filePreview = payload?.filePreview || null;

      if (!threadId) {
        return state;
      }

      const ensuredThread = ensureThreadContentItem({ state, threadId });

      if (!ensuredThread) {
        return state;
      }

      const existingThread = ensuredThread.threadContentItem;
      const nextThreadContent = ensuredThread.threadContent;

      return {
        ...state,
        threadContent: upsertThreadContentItem(nextThreadContent, {
          ...existingThread,
          threadId,
          filePreview
        })
      };
    },

    pieveSetThreadTitle: (state, payload) => {
      const threadId = payload?.threadId;
      const title = payload?.title;
      const updatedAt = payload?.updatedAt;

      return applyThreadTitleMutation({ state, threadId, title, updatedAt });
    },

    pieveSetThreadPinned: (state, payload) => {
      const threadId = payload?.threadId;
      const pinned = payload?.pinned;
      const updatedAt = payload?.updatedAt;

      return applyThreadPinMutation({ state, threadId, pinned, updatedAt });
    },

    pieveSetThreadComposerSettings: (state, payload) => {
      const threadId = payload?.threadId;
      const composerModel = payload?.composerModel;
      const composerReasoningEffort = payload?.composerReasoningEffort;
      const updatedAt = payload?.updatedAt;

      return applyThreadComposerMutation({
        state,
        threadId,
        composerModel,
        composerReasoningEffort,
        updatedAt
      });
    },

    pieveSetThreadSandboxMode: (state, payload) => {
      const threadId = payload?.threadId;
      const sandboxMode = payload?.sandboxMode;
      const updatedAt = payload?.updatedAt;

      return applyThreadSandboxModeMutation({
        state,
        threadId,
        sandboxMode,
        updatedAt
      });
    },

    pieveSetThreadDebugMode: (state, payload) => {
      const threadId = payload?.threadId;
      const debugMode = payload?.debugMode;
      const updatedAt = payload?.updatedAt;

      return applyThreadDebugModeMutation({
        state,
        threadId,
        debugMode,
        updatedAt
      });
    }
  },
  {
    key: 'pieve-session',
    whitelist: ['sidebar', 'projects', 'threads', 'threadsScopeCompanyId', 'appsMetadataItems', 'appsMetadataEtag', 'appsMetadataIncludesHidden', 'knowledge', 'composerDraftsByContextKey'],
    version: 2,
    migrations: {
      2: migratePersistedPieveStateV2
    }
  }
);
